import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Tooltip,
  Collapse,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Divider,
  DialogActions,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AttachFile as AttachFileIcon,
  Payment as PaymentIcon,
  Note as NoteIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  Cancel as CancelIcon,
  Fingerprint as FingerprintIcon,
  Work as WorkIcon,
  ExpandLess as ExpandLessIcon,
  AttachMoney as AttachMoneyIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Case, Document } from '../../../types';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { updateCase, handleDownload, deleteDocument } from '../../../services/firebase';
import AddCaseForm from './AddCaseForm';
import AddPaymentForm from './AddPaymentForm';
import AddNoteForm from './AddNoteForm';
import DocumentUpload from './DocumentUpload';
import EditCaseAmountDialog from './EditCaseAmountDialog';
import EditBiometricDialog from './EditBiometricDialog';

interface CaseListProps {
  cases: Case[];
  clientId: string;
}

const CaseList: React.FC<CaseListProps> = ({ cases, clientId }) => {
  const theme = useTheme();
  const [expandedCase, setExpandedCase] = useState<string | null>(null);
  const [showAddCase, setShowAddCase] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState<string | null>(null);
  const [showAddNote, setShowAddNote] = useState<string | null>(null);
  const [showUploadDoc, setShowUploadDoc] = useState<string | null>(null);
  const [editingCaseAmount, setEditingCaseAmount] = useState<Case | null>(null);
  const [deleteDocDialog, setDeleteDocDialog] = useState<{
    open: boolean;
    caseId: string;
    doc: Document | null;
  }>({
    open: false,
    caseId: '',
    doc: null,
  });
  const [biometricDialogOpen, setBiometricDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);

  const handleExpandCase = (caseId: string) => {
    setExpandedCase(expandedCase === caseId ? null : caseId);
  };

  const handleStatusToggle = async (caseItem: Case) => {
    try {
      await updateCase(caseItem.id, {
        status: caseItem.status === 'Active' ? 'Closed' : 'Active'
      });
      toast.success('Case status updated successfully');
    } catch (error) {
      console.error('Error updating case status:', error);
      toast.error('Failed to update case status');
    }
  };

  const calculateDaysElapsed = (applicationDate: string): number => {
    const appDate = new Date(applicationDate);
    const today = new Date();
    return Math.floor((today.getTime() - appDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const calculateWorkPermitDays = (applicationDate: string): number => {
    const appDate = new Date(applicationDate);
    const today = new Date();
    const daysPassed = Math.floor((today.getTime() - appDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, 150 - daysPassed);
  };

  const calculateEligibilityDate = (applicationDate: string): string => {
    const appDate = new Date(applicationDate);
    const eligibilityDate = new Date(appDate);
    eligibilityDate.setDate(eligibilityDate.getDate() + 150);
    return eligibilityDate.toISOString().split('T')[0];
  };

  const handleMarkBiometric = (caseItem: Case) => {
    setSelectedCase(caseItem);
    setBiometricDialogOpen(true);
  };

  const handleDeleteDocument = async (caseItem: Case, doc: Document) => {
    setDeleteDocDialog({
      open: true,
      caseId: caseItem.id,
      doc,
    });
  };

  const confirmDeleteDocument = async () => {
    if (!deleteDocDialog.doc || !deleteDocDialog.caseId) return;
    
    try {
      const { caseId, doc } = deleteDocDialog;
      await deleteDocument(clientId, caseId, doc.path);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleteDocDialog({ open: false, caseId: '', doc: null });
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" color="text.secondary">
          Case History
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddCase(true)}
          sx={{
            borderRadius: '12px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
          }}
        >
          Add New Case
        </Button>
      </Stack>

      <Stack spacing={2}>
        {cases.map((caseItem) => {
          const isExpanded = expandedCase === caseItem.id;
          const daysElapsed = calculateDaysElapsed(caseItem.applicationDate);
          const totalPaid = caseItem.payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
          const remainingBalance = (caseItem.amountCharged || 0) - totalPaid;

          return (
            <Card
              key={caseItem.id}
              elevation={0}
              sx={{
                borderRadius: '16px',
                border: `1px solid ${alpha(
                  caseItem.type === 'Asylum' ? theme.palette.primary.main : theme.palette.grey[300],
                  0.1
                )}`,
                bgcolor: caseItem.type === 'Asylum' ? alpha(theme.palette.primary.main, 0.02) : 'background.paper',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        variant="h6"
                        sx={{
                          color: caseItem.type === 'Asylum' ? 'primary.main' : 'text.primary',
                          fontWeight: 600,
                        }}
                      >
                        {caseItem.type}
                      </Typography>
                      <Chip
                        label={caseItem.status}
                        size="small"
                        color={caseItem.status === 'Active' ? 'primary' : 'default'}
                        sx={{
                          borderRadius: '8px',
                          fontWeight: 500,
                        }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Toggle Status">
                        <IconButton
                          size="small"
                          onClick={() => handleStatusToggle(caseItem)}
                          sx={{
                            bgcolor: alpha(
                              caseItem.status === 'Active' ? theme.palette.success.main : theme.palette.error.main,
                              0.1
                            ),
                            '&:hover': {
                              bgcolor: alpha(
                                caseItem.status === 'Active' ? theme.palette.success.main : theme.palette.error.main,
                                0.2
                              ),
                            },
                          }}
                        >
                          {caseItem.status === 'Active' ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <CancelIcon color="error" />
                          )}
                        </IconButton>
                      </Tooltip>
                      {!caseItem.biometricTaken && (
                        <Tooltip title="Mark Biometric Taken">
                          <IconButton
                            size="small"
                            onClick={() => handleMarkBiometric(caseItem)}
                            sx={{
                              color: theme.palette.primary.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                              },
                            }}
                          >
                            <FingerprintIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {caseItem.biometricTaken && (
                        <Tooltip title="Edit Biometric Date">
                          <IconButton
                            size="small"
                            onClick={() => handleMarkBiometric(caseItem)}
                            sx={{
                              color: theme.palette.success.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.success.main, 0.08),
                              },
                            }}
                          >
                            <FingerprintIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleExpandCase(caseItem.id)}
                        sx={{
                          transform: isExpanded ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.3s ease',
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Stack>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    divider={<Divider orientation="vertical" flexItem />}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Receipt Number
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {caseItem.receiptNumber}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Application Date
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {formatDate(caseItem.applicationDate)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Days Elapsed
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {daysElapsed} days
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    divider={<Divider orientation="vertical" flexItem />}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Amount Charged
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body1" fontWeight={600} color="primary.main">
                          {formatCurrency(caseItem.amountCharged)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCaseAmount(caseItem);
                          }}
                          sx={{
                            color: theme.palette.primary.main,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Paid
                      </Typography>
                      <Typography variant="body1" fontWeight={600} color="success.main">
                        {formatCurrency(totalPaid)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Balance
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        color={remainingBalance > 0 ? 'error.main' : 'success.main'}
                      >
                        {formatCurrency(remainingBalance)}
                      </Typography>
                    </Box>
                  </Stack>

                  <Collapse in={isExpanded}>
                    <Stack spacing={3} mt={2}>
                      <Divider />
                      
                      <Stack direction="row" spacing={2}>
                        <Button
                          startIcon={<PaymentIcon />}
                          onClick={() => setShowAddPayment(caseItem.id)}
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            borderColor: alpha(theme.palette.success.main, 0.5),
                            color: 'success.main',
                            '&:hover': {
                              borderColor: 'success.main',
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                            },
                          }}
                        >
                          Add Payment
                        </Button>
                        <Button
                          startIcon={<NoteIcon />}
                          onClick={() => setShowAddNote(caseItem.id)}
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            borderColor: alpha(theme.palette.info.main, 0.5),
                            color: 'info.main',
                            '&:hover': {
                              borderColor: 'info.main',
                              bgcolor: alpha(theme.palette.info.main, 0.1),
                            },
                          }}
                        >
                          Add Note
                        </Button>
                        <Button
                          startIcon={<AttachFileIcon />}
                          onClick={() => setShowUploadDoc(caseItem.id)}
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            borderColor: alpha(theme.palette.warning.main, 0.5),
                            color: 'warning.main',
                            '&:hover': {
                              borderColor: 'warning.main',
                              bgcolor: alpha(theme.palette.warning.main, 0.1),
                            },
                          }}
                        >
                          Upload Document
                        </Button>
                      </Stack>

                      {caseItem.notes && caseItem.notes.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Notes
                          </Typography>
                          <Stack spacing={1}>
                            {caseItem.notes.map((note) => (
                              <Card
                                key={note.id}
                                variant="outlined"
                                sx={{
                                  borderRadius: '8px',
                                  bgcolor: alpha(theme.palette.info.main, 0.05),
                                }}
                              >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                  <Typography variant="body2">{note.content}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(note.timestamp)}
                                  </Typography>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {caseItem.documents && caseItem.documents.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Documents
                          </Typography>
                          <Stack spacing={1}>
                            {caseItem.documents.map((doc) => (
                              <Card
                                key={doc.id}
                                variant="outlined"
                                sx={{
                                  borderRadius: '8px',
                                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                                }}
                              >
                                <CardContent
                                  sx={{
                                    p: 2,
                                    '&:last-child': { pb: 2 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <DescriptionIcon color="warning" />
                                    <Box>
                                      <Typography variant="body2">{doc.name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDate(doc.createdAt.toDate())}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                  <Stack direction="row" spacing={1}>
                                    <IconButton
                                      size="small"
                                      onClick={() => window.open(doc.url, '_blank')}
                                      sx={{
                                        color: theme.palette.warning.main,
                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.warning.main, 0.2),
                                        },
                                      }}
                                    >
                                      <DescriptionIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteDocument(caseItem, doc)}
                                      sx={{
                                        color: theme.palette.error.main,
                                        bgcolor: alpha(theme.palette.error.main, 0.1),
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.error.main, 0.2),
                                        },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {caseItem.type === 'Asylum' && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Days Until Work Permit Application Eligibility
                          </Typography>
                          <Typography>
                            {calculateWorkPermitDays(caseItem.applicationDate)} days
                          </Typography>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                            Eligible Date
                          </Typography>
                          <Typography>
                            {formatDate(calculateEligibilityDate(caseItem.applicationDate))}
                          </Typography>
                        </Box>
                      )}

                      {caseItem.biometricTaken && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Biometric Taken On
                          </Typography>
                          <Typography>{formatDate(caseItem.biometricDate || '')}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </Collapse>
                </Stack>
              </CardContent>
            </Card>
          );
        })}

        {cases.length === 0 && (
          <Card
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.grey[300], 0.2)}`,
              bgcolor: 'background.paper',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No cases found</Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      <Dialog
        open={showAddCase}
        onClose={() => setShowAddCase(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add New Case
          <IconButton
            onClick={() => setShowAddCase(false)}
            aria-label="Close"
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <AddCaseForm
            clientId={clientId}
            onCancel={() => setShowAddCase(false)}
            onSuccess={() => {
              setShowAddCase(false);
              window.location.reload();
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!showAddPayment}
        onClose={() => setShowAddPayment(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Payment
          <IconButton
            onClick={() => setShowAddPayment(null)}
            aria-label="Close"
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {showAddPayment && (
            <AddPaymentForm
              caseId={showAddPayment}
              initialBalance={(() => {
                const c = cases.find((x) => x.id === showAddPayment);
                if (!c) return 0;
                const paid = c.payments.reduce(
                  (s, p) => s + (Number(p.amount) || 0),
                  0,
                );
                return c.amountCharged - paid;
              })()}
              onCancel={() => setShowAddPayment(null)}
              onPaymentAdded={() => setShowAddPayment(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!showAddNote}
        onClose={() => setShowAddNote(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add Note
          <IconButton
            onClick={() => setShowAddNote(null)}
            aria-label="Close"
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {showAddNote && (
            <AddNoteForm
              caseId={showAddNote}
              onCancel={() => setShowAddNote(null)}
              onSuccess={() => {
                setShowAddNote(null);
                window.location.reload();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!showUploadDoc}
        onClose={() => setShowUploadDoc(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload Document
          <IconButton
            onClick={() => setShowUploadDoc(null)}
            aria-label="Close"
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {showUploadDoc && (
            <DocumentUpload
              caseId={showUploadDoc}
              onSuccess={() => setShowUploadDoc(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDocDialog.open}
        onClose={() => setDeleteDocDialog({ open: false, caseId: '', doc: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete Document
          <IconButton
            onClick={() => setDeleteDocDialog({ open: false, caseId: '', doc: null })}
            aria-label="Close"
            size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            Are you sure you want to delete this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setDeleteDocDialog({ open: false, caseId: '', doc: null })}
          >
            Cancel
          </Button>
          <Button onClick={confirmDeleteDocument} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <EditCaseAmountDialog
        open={!!editingCaseAmount}
        onClose={() => setEditingCaseAmount(null)}
        caseData={editingCaseAmount!}
        onAmountUpdated={() => {
          setEditingCaseAmount(null);
          window.location.reload();
        }}
      />

      {selectedCase && (
        <EditBiometricDialog
          open={biometricDialogOpen}
          onClose={() => {
            setBiometricDialogOpen(false);
            setSelectedCase(null);
          }}
          caseData={selectedCase}
        />
      )}
    </Box>
  );
};

export default CaseList; 